import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ShieldCheck, Download, AlertTriangle, CheckCircle2, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import useGoogleAdsConversion from "../hooks/useGoogleAdsConversion";

/**
 * /hull-scan/unlocked/:scan_id — landing page after the Stripe success
 * redirect. Polls /full-report (which self-heals on first call) and
 * renders the full forensic + the sealed CTA into the boarding flow.
 */
export default function HullScanUnlocked() {
    const { scan_id } = useParams();
    const [search] = useSearchParams();
    const session_id = search.get("session_id");
    const [report, setReport] = useState(null);
    const [error, setError] = useState("");
    const [polling, setPolling] = useState(true);

    // Fire Google Ads $49 Hull-Scan conversion only when the report
    // unlocks successfully (proves the Stripe payment was captured).
    useGoogleAdsConversion({
        event: "hull_scan",
        armed: !!report,
        value: 49,
        currency: "USD",
        transactionId: session_id || scan_id,
    });

    useEffect(() => {
        let attempts = 0; let cancelled = false;
        const poll = async () => {
            attempts += 1;
            try {
                const url = session_id
                    ? `/hull-scan/${scan_id}/full-report?session_id=${encodeURIComponent(session_id)}`
                    : `/hull-scan/${scan_id}/full-report`;
                const { data } = await api.get(url);
                if (cancelled) return;
                setReport(data); setPolling(false); setError("");
            } catch (e) {
                if (cancelled) return;
                if (attempts >= 8) {
                    setError(e.response?.data?.detail || "Could not unlock report.");
                    setPolling(false);
                    return;
                }
                setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [scan_id, session_id]);

    const downloadJson = () => {
        if (!report) return;
        const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `hull-forensic-${report.summary?.host || scan_id}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
        toast.success("Sealed forensic downloaded.");
    };

    if (polling) {
        return <div className="min-h-screen flex items-center justify-center text-center px-4" data-testid="hull-unlock-loading">
            <div>
                <div className="font-heading text-3xl text-[hsl(45_85%_70%)] animate-pulse">Verifying Strike Fee…</div>
                <div className="text-muted-foreground text-sm mt-2">Stripe webhook landing. Hold position.</div>
            </div>
        </div>;
    }
    if (error || !report) {
        return <div className="min-h-screen flex items-center justify-center text-center px-4">
            <div>
                <div className="font-heading text-3xl text-[hsl(350_75%_75%)]">Unlock Failed</div>
                <div className="text-muted-foreground text-sm mt-2">{error || "Report unavailable."}</div>
                <Link to="/" className="inline-block mt-4 px-4 py-2 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_15%)]">Back to Bridge</Link>
            </div>
        </div>;
    }

    const { scores = {}, findings = [], summary = {}, sovereign_seal: seal = {} } = report;
    return (
        <div className="max-w-5xl mx-auto px-4 py-12 space-y-8" data-testid="hull-unlock-page">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(151_60%_70%)]">
                <CheckCircle2 size={12} /> Delivery Fee Captured · Forensic Unlocked
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter">Full Forensic · {summary.host}</h1>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Score label="Performance"   value={scores.performance}   accent="hsl(45 85% 65%)" />
                <Score label="Security"      value={scores.security}      accent="hsl(151 60% 60%)" />
                <Score label="Intelligence"  value={scores.intelligence}  accent="hsl(195 70% 65%)" />
                <Score label="Overall"       value={scores.overall}       accent="hsl(280 60% 70%)" />
            </div>

            <div className="rounded-2xl border border-[hsl(40_50%_35%/0.4)] bg-[hsl(151_55%_8%/0.6)] p-6">
                <div className="font-heading text-2xl mb-4 flex items-center gap-2"><AlertTriangle size={18} className="text-[hsl(45_85%_70%)]" /> Findings · {findings.length}</div>
                {findings.length === 0 ? <div className="text-sm text-muted-foreground">No issues detected — your hull is solid.</div> : (
                    <div className="space-y-3">
                        {findings.map((f, i) => (
                            <div key={i} className="flex items-start gap-3 text-sm">
                                <span className={`shrink-0 mt-0.5 px-2 py-0.5 rounded-full uppercase tracking-wider text-[9px] ${
                                    f.severity === "critical" ? "bg-[hsl(350_55%_25%/0.6)] text-[hsl(350_75%_75%)]" :
                                    f.severity === "warning" ? "bg-[hsl(45_50%_22%/0.6)] text-[hsl(45_85%_72%)]" :
                                    "bg-[hsl(195_50%_18%/0.5)] text-[hsl(195_70%_70%)]"
                                }`}>{f.severity}</span>
                                <div>
                                    <div className="font-semibold">{f.label} <span className="text-[10px] text-muted-foreground uppercase ml-1">· {f.dimension}</span></div>
                                    <div className="text-muted-foreground">{f.message}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="rounded-2xl border-2 border-[hsl(45_75%_55%/0.4)] bg-[hsl(40_50%_8%/0.5)] p-6 flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_70%)] flex items-center gap-1.5"><ShieldCheck size={12} /> Sovereign Seal</div>
                    <div className="font-heading text-lg mt-1">EIGOO authored this report.</div>
                    <div className="text-[11px] font-mono text-[hsl(45_85%_72%)] mt-2 break-all">sha256: {seal.sha256}</div>
                    <div className="text-[11px] font-mono text-[hsl(45_85%_72%)] break-all">sig:    {seal.signature}</div>
                </div>
                <button onClick={downloadJson} data-testid="hull-unlock-download" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(45_75%_62%)]"><Download size={13} /> Download Sealed JSON</button>
            </div>

            <div className="rounded-2xl border border-[hsl(151_55%_30%/0.5)] bg-[hsl(151_55%_10%/0.6)] p-6">
                <div className="font-heading text-xl">Re-Commission Your Hull</div>
                <div className="text-sm text-muted-foreground mt-1">Patch every finding above with a Founding Privateer. Higher tier = full hull rebuild + ongoing operatorship.</div>
                <Link to={`/boarding?source=hull-scan&host=${encodeURIComponent(summary.host || "")}`} data-testid="hull-unlock-boarding" className="inline-flex items-center gap-2 mt-4 px-5 py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-sm uppercase tracking-wider hover:bg-[hsl(45_75%_62%)]">Board the Shipyard <ArrowRight size={14} /></Link>
            </div>
        </div>
    );
}

function Score({ label, value, accent }) {
    const grade = value >= 80 ? "A" : value >= 60 ? "B" : value >= 40 ? "C" : "F";
    return (
        <div className="rounded-xl border border-[hsl(40_50%_35%/0.3)] bg-[hsl(151_55%_10%/0.6)] p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className="font-heading text-3xl mt-1" style={{ color: accent }}>{value}<span className="text-sm text-muted-foreground">/100 · {grade}</span></div>
            <div className="mt-2 h-1.5 rounded-full bg-[hsl(40_30%_20%)] overflow-hidden">
                <div className="h-full" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: accent }} />
            </div>
        </div>
    );
}
